import { Plus, X } from "lucide-react";
import React, { useLayoutEffect, useState } from "react";
import RecommendationCardSvg from "../../../../assets/svg/RecommendationCardSvg";
import { ButtonV2 } from "../../../../components/ui/button/v2";
import ActionAgainstInvolvedPartiesTable from "../../../recommendations/components/ActionAgainstInvolvedPartiesTable/index";
import ActionsAgainstInvolvedPartiesTableV2 from "../../../recommendations/components/ActionAgainstInvolvedPartiesTable/ActionsAgainstInvolvedPartiesTableV2";
import PolicyImprovementsTable from "../../../recommendations/components/PolicyImprovementsTable";
import { useAppSelector } from "../../../../app/store";
import { isCreditAccountStakeholdersRevampEnabled, isReportsPolicyNormalizationEnabled } from "../../../../features/configs/logic/Slice";
import PolicyImprovementsTableV2 from "../../../../features/recommendations/components/PolicyImprovementsTable/PolicyImprovementsTableV2";

export interface ActionButtonProps {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}

export interface ViewOrAddRecommendationProps {
  caseId: string;
  scrollRef?: React.RefObject<HTMLDivElement>;
  actionAgainstInvolvedPartiesDisabled?: boolean;
  processAndPolicyImprovementsDisabled?: boolean;
  hidePolicyImprovements?: boolean;
}

const ActionButton: React.FC<ActionButtonProps> = ({ label, onClick, disabled }) => (
  <ButtonV2 onClick={onClick} disabled={disabled}>
    <Plus className="w-4 h-4 mr-1" /> {label}
  </ButtonV2>
);

interface ActionsAgainstInvolvedPartiesModalProps {
  caseId: string;
  onClose: () => void;
  scrollRef?: React.RefObject<HTMLDivElement>;
}

export const ActionsAgainstInvolvedPartiesModal: React.FC<ActionsAgainstInvolvedPartiesModalProps> = ({
  caseId,
  onClose,
  scrollRef,
}) => {
  const isCreditAccountStakeholdersRevamp = useAppSelector(isCreditAccountStakeholdersRevampEnabled);

  useLayoutEffect(() => {
    scrollRef?.current?.scrollTo({ top: 0, left: 0 });
  }, [scrollRef]);

  return (
    <div className="absolute inset-0 bg-white flex items-start justify-center pt-12 z-[100]">
      <div className="relative bg-white w-[95%] max-h-[85vh] rounded-xl shadow-xl overflow-auto">
        <button
          onClick={onClose}
          className="fixed top-4 right-16 z-[101] w-8 h-8 flex items-center justify-center rounded-full border border-[#E5E5E5] bg-white text-gray-500 hover:bg-gray-50 transition"
        >
          <X size={18} />
        </button>

        <div className="p-6">
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-[#0A0A0A]">Actions Against Involved Parties</h3>
            <p className="text-sm text-gray-500 mt-1">
              Manage actions and their implementation status for involved parties
            </p>
          </div>

          <div className="min-h-[400px]">
            {isCreditAccountStakeholdersRevamp ? (
              <ActionsAgainstInvolvedPartiesTableV2 caseId={caseId} />
            ) : (
              <ActionAgainstInvolvedPartiesTable caseId={caseId} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

interface PolicyImprovementsModalProps {
  caseId: string;
  onClose: () => void;
  scrollRef?: React.RefObject<HTMLDivElement>;
}

export const PolicyImprovementsModal: React.FC<PolicyImprovementsModalProps> = ({ caseId, onClose, scrollRef }) => {
  useLayoutEffect(() => {
    scrollRef?.current?.scrollTo({ top: 0, left: 0 });
  }, [scrollRef]);

  const isPolicyImprovementV2Enabled = useAppSelector(isReportsPolicyNormalizationEnabled);

  return (
    <div className="absolute inset-0 bg-white flex items-start justify-center pt-12 z-[100]">
      <div className="relative bg-white w-[95%] max-h-[85vh] rounded-xl shadow-xl overflow-auto">
        <button
          onClick={onClose}
          className="fixed top-4 right-16 z-[101] w-8 h-8 flex items-center justify-center rounded-full border border-[#E5E5E5] bg-white text-gray-500 hover:bg-gray-50 transition"
        >
          <X size={18} />
        </button>

        <div className="p-6">
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-[#0A0A0A]">Process & Policy Improvements</h3>
            <p className="text-sm text-gray-500 mt-1">Track suggested policy changes and their approval status</p>
          </div>

          <div className="min-h-[400px]">
          {
            isPolicyImprovementV2Enabled ? (
              <PolicyImprovementsTableV2 caseId={caseId} />
            ) : (
              <PolicyImprovementsTable caseId={caseId} />
            )
          }
          </div>
        </div>
      </div>
    </div>
  );
};

const ViewOrAddRecommendation: React.FC<ViewOrAddRecommendationProps> = ({
  caseId,
  scrollRef,
  actionAgainstInvolvedPartiesDisabled = false,
  processAndPolicyImprovementsDisabled = false,
  hidePolicyImprovements = false,
}) => {
  const [isActionsModalOpen, setIsActionsModalOpen] = useState(false);
  const [isPolicyModalOpen, setIsPolicyModalOpen] = useState(false);

  const openActionsModal = () => setIsActionsModalOpen(true);
  const closeActionsModal = () => setIsActionsModalOpen(false);

  const openPolicyModal = () => setIsPolicyModalOpen(true);
  const closePolicyModal = () => setIsPolicyModalOpen(false);

  const renderCard = () => (
    <div className="rounded-lg border border-gray-200 bg-[#F3F4F6] flex flex-row items-center justify-between gap-4">
      <div className="flex flex-col gap-4 flex-1 p-4">
        <div className="flex flex-col gap-1">
          <h3 className="text-base font-semibold text-black">View or Add Recommendations</h3>
          <p className="text-sm text-gray-500">
            Add recommendations for Actions Against Involved Parties and Process & Policy Improvements
          </p>
        </div>

        <div className="flex gap-3">
          <ActionButton
            label="Action Against Involved Parties"
            onClick={openActionsModal}
            disabled={actionAgainstInvolvedPartiesDisabled}
          />
          {!hidePolicyImprovements && (
            <ActionButton
              label="Process & Policy Improvements"
              onClick={openPolicyModal}
              disabled={processAndPolicyImprovementsDisabled}
            />
          )}
        </div>
      </div>

      <div className="flex-shrink-0 opacity-80 pointer-events-none select-none">
        <RecommendationCardSvg />
      </div>
    </div>
  );

  return (
    <>
      {renderCard()}

      {isActionsModalOpen && (
        <ActionsAgainstInvolvedPartiesModal caseId={caseId} onClose={closeActionsModal} scrollRef={scrollRef} />
      )}

      {isPolicyModalOpen && (
        <PolicyImprovementsModal caseId={caseId} onClose={closePolicyModal} scrollRef={scrollRef} />
      )}
    </>
  );
};

export default ViewOrAddRecommendation;
